import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../../shared/page-layout/page-hero.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../../services/session-storage.service';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';
import { BankReconciliationService } from '../bank-reconciliation.service';
import { BankReconciliationRule } from '../models/bank-reconciliation.model';

@Component({
    selector: 'app-bank-reconciliation-rules',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, SelectModule, TableModule, TagModule,
        PageShellComponent, PageHeroComponent, PageMetricStripComponent, PageDataPanelComponent],
    template: `
        <app-page-shell>
            <app-page-hero eyebrow="Bank Reconciliation" title="Auto-Match Rules" subtitle="Configure explainable matching rules, confidence thresholds, and safe auto-apply behavior.">
                <div actions>
                    <button pButton type="button" icon="pi pi-plus" label="New Rule" (click)="router.navigate(['/accounts/enterprise/bank-reconciliation/rules/new'])"></button>
                </div>
            </app-page-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <app-page-data-panel title="Rules" [resultText]="rules.length + ' rules'">
                <div actions class="toolbar">
                    <p-select [options]="bankAccounts" [(ngModel)]="selectedBankAccountId" optionLabel="accountName" optionValue="id"
                              placeholder="All scopes" [showClear]="true" (onChange)="loadRules()"></p-select>
                </div>
                <p-table [value]="rules" [loading]="loading" styleClass="p-datatable-sm" [paginator]="true" [rows]="15">
                    <ng-template pTemplate="header">
                        <tr>
                            <th>Rule</th><th>Scope</th><th>Criteria</th><th>Confidence</th><th>Status</th><th></th>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-rule>
                        <tr>
                            <td>
                                <strong>{{ rule.priority }}. {{ rule.ruleName }}</strong>
                                <div class="muted">Rule #{{ rule.ruleId || '-' }}</div>
                            </td>
                            <td>{{ accountName(rule.bankAccountId) }}</td>
                            <td>
                                <div class="chips">
                                    <span *ngIf="rule.matchExactAmount">Exact amount</span>
                                    <span *ngIf="!rule.matchExactAmount">Tolerance {{ rule.amountTolerance | number:'1.2-2' }}</span>
                                    <span *ngIf="rule.matchReference">Reference</span>
                                    <span *ngIf="rule.matchCounterparty">Counterparty</span>
                                    <span *ngIf="rule.bankReferenceContains || rule.descriptionContains || rule.counterpartyContains || rule.bookReferenceContains">Contains</span>
                                </div>
                            </td>
                            <td>
                                <div>Min {{ rule.minimumConfidenceScore | number:'1.2-4' }}</div>
                                <div class="muted">Auto {{ rule.autoMatchThreshold | number:'1.2-4' }}</div>
                            </td>
                            <td>
                                <p-tag [value]="rule.active ? 'ACTIVE' : 'INACTIVE'" [severity]="rule.active ? 'success' : 'secondary'"></p-tag>
                                <p-tag class="tag-gap" [value]="rule.autoApply ? 'AUTO APPLY' : 'PREVIEW ONLY'" [severity]="rule.autoApply ? 'warn' : 'info'"></p-tag>
                            </td>
                            <td class="actions">
                                <button pButton type="button" icon="pi pi-pencil" label="Edit" [text]="true" (click)="editRule(rule)"></button>
                                <button pButton type="button" icon="pi pi-trash" label="Delete" [text]="true" severity="danger" (click)="deleteRule(rule)"></button>
                            </td>
                        </tr>
                    </ng-template>
                    <ng-template pTemplate="emptymessage">
                        <tr><td colspan="6">No auto-match rules configured.</td></tr>
                    </ng-template>
                </p-table>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .toolbar { display: flex; gap: .5rem; flex-wrap: wrap; }
        .toolbar p-select { min-width: 240px; }
        .muted { color: #64748b; font-size: .78rem; margin-top: .2rem; }
        .chips { display: flex; gap: .35rem; flex-wrap: wrap; }
        .chips span { background: #ecfeff; color: #0f766e; padding: .2rem .45rem; border-radius: 999px; font-size: .75rem; }
        .tag-gap { margin-left: .35rem; }
        .actions { text-align: right; white-space: nowrap; }
    `]
})
export class BankReconciliationRulesComponent implements OnInit {
    rules: BankReconciliationRule[] = [];
    bankAccounts: BankAccount[] = [];
    selectedBankAccountId?: number;
    loading = false;
    metrics: PageMetric[] = [];

    constructor(
        private bankReconciliationService: BankReconciliationService,
        private bankAccountService: BankAccountService,
        private sessionStorageService: SessionStorageService,
        public router: Router
    ) {}

    ngOnInit(): void {
        this.loadBankAccounts();
        this.loadRules();
    }

    loadBankAccounts(): void {
        this.bankAccountService.getBankAccounts('Company', this.companyId(), '', 0, 100).subscribe(result => this.bankAccounts = result.items ?? []);
    }

    loadRules(): void {
        this.loading = true;
        this.bankReconciliationService.getRules(this.companyId(), this.selectedBankAccountId).subscribe({
            next: rules => {
                this.rules = rules ?? [];
                this.metrics = [
                    { label: 'Rules', value: this.rules.length, tone: 'info' },
                    { label: 'Active', value: this.rules.filter(rule => rule.active).length, tone: 'success' },
                    { label: 'Auto apply', value: this.rules.filter(rule => rule.autoApply).length, tone: 'warn' },
                    { label: 'Scoped', value: this.rules.filter(rule => !!rule.bankAccountId).length, tone: 'info' }
                ];
                this.loading = false;
            },
            error: () => this.loading = false
        });
    }

    editRule(rule: BankReconciliationRule): void {
        this.router.navigate(['/accounts/enterprise/bank-reconciliation/rules', rule.ruleId, 'edit']);
    }

    deleteRule(rule: BankReconciliationRule): void {
        if (!rule.ruleId || !confirm(`Delete rule "${rule.ruleName}"?`)) return;
        this.bankReconciliationService.deleteRule(rule.ruleId).subscribe(() => this.loadRules());
    }

    accountName(bankAccountId?: number): string {
        if (!bankAccountId) return 'All bank accounts';
        return this.bankAccounts.find(account => account.id === bankAccountId)?.accountName ?? `Account ${bankAccountId}`;
    }

    private companyId(): number {
        return this.sessionStorageService.getCompany()?.id ?? 0;
    }
}
